import React, { useEffect, useMemo, useState } from 'react';
import { X, FileText, Loader2, AlertCircle, Plus, Trash2, Info } from 'lucide-react';
import styles from '../Vendors.module.css';
import ProductPicker from './ProductPicker';
import {
    vendorFetch, errText, INVENTORY_API, inr, inr2, ymd, addDaysYmd, r2, MODES, CLEARS_AT_ONCE, GST_RATES,
} from '../../../utils/vendorApi';

const newLine = (p = null) => ({ key: Math.random(), productId: p?._id || '', qty: 1, unitCost: p ? Number(p.pricing?.purchasePrice) || '' : '', gstRate: 0 });

/**
 * Vendor purchase bill: saving it adds the stock.
 * GST per line is optional (leave 0 to track only the total).
 */
export default function PurchaseBillModal({ storeId, vendorId: presetVendor = '', productId: presetProduct = '', canPay = true, onClose, onSaved }) {
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendorId, setVendorId] = useState(presetVendor);
    const [billNo, setBillNo] = useState('');
    const [billDate, setBillDate] = useState(ymd());
    const [dueDate, setDueDate] = useState('');
    const [dueTouched, setDueTouched] = useState(false);
    const [lines, setLines] = useState([newLine()]);
    const [otherCharges, setOtherCharges] = useState('');
    const [discount, setDiscount] = useState('');
    const [roundOff, setRoundOff] = useState(true);
    const [payType, setPayType] = useState('LATER');      // LATER | FULL | PART
    const [payAmount, setPayAmount] = useState('');
    const [payMode, setPayMode] = useState('CASH');
    const [payRef, setPayRef] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [v, inv] = await Promise.all([
                    vendorFetch('', { storeId }),
                    vendorFetch(`${INVENTORY_API}?storeId=${encodeURIComponent(storeId)}`, { storeId }),
                ]);
                if (!alive) return;
                setVendors(v.data || []);
                const list = inv.data || [];
                setProducts(list);
                if (presetProduct) {
                    const p = list.find((x) => String(x._id) === String(presetProduct));
                    if (p) setLines([newLine(p)]);
                }
            } catch (e) { if (alive) setError(errText(e)); } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [storeId, presetProduct]);

    const vendor = vendors.find((v) => String(v._id) === String(vendorId));
    // due date follows the vendor's payment terms until the user changes it
    useEffect(() => {
        if (!dueTouched) setDueDate(vendor ? addDaysYmd(billDate, vendor.paymentTermsDays || 0) : '');
    }, [vendor, billDate, dueTouched]);

    const calc = useMemo(() => {
        const rows = lines.map((l) => {
            const qty = Math.max(0, Math.floor(Number(l.qty) || 0));
            const cost = Math.max(0, Number(l.unitCost) || 0);
            const taxable = r2(qty * cost);
            const gst = r2(taxable * (Number(l.gstRate) || 0) / 100);
            return { ...l, qty, cost, taxable, gst, total: r2(taxable + gst) };
        });
        const taxable = r2(rows.reduce((s, x) => s + x.taxable, 0));
        const gst = r2(rows.reduce((s, x) => s + x.gst, 0));
        const raw = r2(taxable + gst + (Number(otherCharges) || 0) - (Number(discount) || 0));
        const total = roundOff ? Math.round(raw) : raw;
        return { rows, taxable, gst, raw, total, roundOff: r2(total - raw) };
    }, [lines, otherCharges, discount, roundOff]);

    const advance = Number(vendor?.summary?.advance) || 0;
    const payNow = payType === 'FULL' ? Math.max(0, r2(calc.total - advance)) : payType === 'PART' ? Number(payAmount) || 0 : 0;
    const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    const save = async () => {
        setError('');
        if (!vendorId) { setError('Choose the vendor.'); return; }
        const valid = calc.rows.filter((l) => l.productId);
        if (!valid.length) { setError('Add at least one product.'); return; }
        if (valid.some((l) => l.qty <= 0)) { setError('Every line needs a quantity of 1 or more.'); return; }
        const ids = valid.map((l) => String(l.productId));
        if (new Set(ids).size !== ids.length && !window.confirm('The same product is on more than one line. Save anyway?')) return;
        if (calc.raw < 0) { setError('Discount is more than the bill.'); return; }
        if (payNow > calc.total + 0.01) { setError('Payment now is more than the bill total.'); return; }
        setSaving(true);
        try {
            const json = await vendorFetch('/bills', {
                method: 'POST', storeId,
                body: {
                    vendorId, billNo, billDate, dueDate: dueDate || undefined, roundOff,
                    items: valid.map((l) => ({ productId: l.productId, qty: l.qty, unitCost: l.cost, gstRate: Number(l.gstRate) || 0 })),
                    otherCharges: Number(otherCharges) || 0, discount: Number(discount) || 0, note,
                    ...(payNow > 0 ? { payNow: { amount: payNow, mode: payMode, reference: payRef } } : {}),
                },
            });
            onSaved?.(json.data, json.message);
        } catch (e) { setError(errText(e)); } finally { setSaving(false); }
    };

    const activeVendors = vendors.filter((v) => v.isActive !== false);

    return (
        <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()} role="presentation">
            <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-label="New purchase bill">
                <div className={styles.modalHead}>
                    <span className={styles.modalIcon}><FileText size={18} /></span>
                    <div><h3>New purchase bill</h3><p>Stock is added as soon as you save</p></div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close"><X size={17} /></button>
                </div>

                <div className={styles.modalBody}>
                    {error && <div className={`${styles.msg} ${styles.msgErr}`}><AlertCircle size={14} /><span>{error}</span></div>}
                    {loading ? <div className={styles.empty}><Loader2 size={20} className={styles.spin} /></div> : (
                        <>
                            <div className={styles.grid4}>
                                <label className={styles.field}>
                                    <span>Vendor <em>*</em></span>
                                    <select className={styles.input} value={vendorId} onChange={(e) => setVendorId(e.target.value)} autoFocus={!presetVendor}>
                                        <option value="">Choose vendor</option>
                                        {activeVendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                                    </select>
                                    {!activeVendors.length && <small>No vendors yet. Add one on the Vendors page.</small>}
                                </label>
                                <label className={styles.field}><span>Vendor bill no.</span><input className={styles.input} value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder="From their invoice" maxLength={60} /></label>
                                <label className={styles.field}><span>Bill date</span><input className={styles.input} type="date" value={billDate} max={ymd()} onChange={(e) => setBillDate(e.target.value)} /></label>
                                <label className={styles.field}>
                                    <span>Due date</span>
                                    <input className={styles.input} type="date" value={dueDate} min={billDate} onChange={(e) => { setDueDate(e.target.value); setDueTouched(true); }} />
                                    {vendor && <small>{vendor.paymentTermsDays ? `${vendor.paymentTermsDays} days credit` : 'Pay on delivery'}</small>}
                                </label>
                            </div>

                            {vendor && (vendor.summary?.balance > 0.009 || advance > 0.009) && (
                                <div className={styles.note}>
                                    <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                    <span>
                                        {vendor.summary?.balance > 0.009 && <>You already owe {vendor.name} <b>{inr(vendor.summary.balance)}</b>. </>}
                                        {advance > 0.009 && <>Advance of <b>{inr(advance)}</b> with this vendor will be used on this bill automatically.</>}
                                    </span>
                                </div>
                            )}

                            <div>
                                <div className={styles.sectionLabel} style={{ marginBottom: 6 }}>Products received</div>
                                <div className={styles.lines}>
                                    <div className={styles.lineHead}><span>Product</span><span>Qty</span><span>Cost / unit (₹)</span><span>GST</span><span style={{ textAlign: 'right' }}>Amount</span><span /></div>
                                    {calc.rows.map((l, i) => (
                                        <div key={l.key} className={styles.line}>
                                            <ProductPicker products={products} value={l.productId} autoFocus={Boolean(presetVendor) && i === 0 && !l.productId}
                                                onPick={(p) => setLine(l.key, { productId: p._id, unitCost: l.unitCost === '' || !l.productId ? Number(p.pricing?.purchasePrice) || '' : l.unitCost })} />
                                            <input className={styles.input} type="number" min="1" step="1" value={lines[i].qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} aria-label="Quantity" />
                                            <input className={styles.input} type="number" min="0" step="0.01" value={lines[i].unitCost} onChange={(e) => setLine(l.key, { unitCost: e.target.value })} aria-label="Cost per unit" placeholder="0" />
                                            <select className={styles.input} value={lines[i].gstRate} onChange={(e) => setLine(l.key, { gstRate: Number(e.target.value) })} aria-label="GST">
                                                {GST_RATES.map((g) => <option key={g} value={g}>{g ? `${g}%` : 'No GST'}</option>)}
                                            </select>
                                            <span className={styles.lineTotal}>{inr2(l.total)}{l.gst > 0 && <small>incl. GST {inr2(l.gst)}</small>}</span>
                                            <button type="button" className={styles.removeBtn} onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [newLine()]))} aria-label="Remove line"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                    <button type="button" className={styles.addLine} onClick={() => setLines((ls) => [...ls, newLine()])}><Plus size={14} /> Add product</button>
                                </div>
                            </div>

                            <div className={styles.billBottom}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div className={styles.grid3}>
                                        <label className={styles.field}><span>Freight / other charges (₹)</span><input className={styles.input} type="number" min="0" value={otherCharges} onChange={(e) => setOtherCharges(e.target.value)} placeholder="0" /></label>
                                        <label className={styles.field}><span>Discount (₹)</span><input className={styles.input} type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" /></label>
                                        <label className={styles.field}><span>Round off</span>
                                            <select className={styles.input} value={roundOff ? '1' : '0'} onChange={(e) => setRoundOff(e.target.value === '1')}><option value="1">Round to ₹1</option><option value="0">Exact paise</option></select>
                                        </label>
                                    </div>
                                    {canPay && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div className={styles.sectionLabel}>Payment</div>
                                            <div className={styles.segment}>
                                                {[['LATER', 'Pay later'], ['FULL', 'Paid in full'], ['PART', 'Paid part']].map(([k, label]) => (
                                                    <button key={k} type="button" className={payType === k ? styles.segOn : ''} onClick={() => setPayType(k)}>{label}</button>
                                                ))}
                                            </div>
                                            {payType !== 'LATER' && (
                                                <div className={styles.grid3}>
                                                    <label className={styles.field}><span>Amount paid now (₹)</span>
                                                        <input className={styles.input} type="number" min="0" value={payType === 'FULL' ? payNow : payAmount} disabled={payType === 'FULL'} onChange={(e) => setPayAmount(e.target.value)} />
                                                    </label>
                                                    <label className={styles.field}><span>Paid by</span>
                                                        <select className={styles.input} value={payMode} onChange={(e) => setPayMode(e.target.value)}>{MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
                                                        <small>{CLEARS_AT_ONCE.includes(payMode) ? (payMode === 'CASH' ? 'Taken from the cash drawer.' : 'Marked cleared.') : 'Marked "not cleared" until you confirm it.'}</small>
                                                    </label>
                                                    <label className={styles.field}><span>Reference</span><input className={styles.input} value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="UTR / cheque no." maxLength={80} /></label>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <label className={styles.field}><span>Note</span><input className={styles.input} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Optional" /></label>
                                </div>
                                <div className={styles.totals}>
                                    <div><span>Items</span><b>{inr2(calc.taxable)}</b></div>
                                    {calc.gst > 0 && <div><span>GST (input)</span><b>{inr2(calc.gst)}</b></div>}
                                    {Number(otherCharges) > 0 && <div><span>Other charges</span><b>{inr2(otherCharges)}</b></div>}
                                    {Number(discount) > 0 && <div><span>Discount</span><b>−{inr2(discount)}</b></div>}
                                    {Math.abs(calc.roundOff) > 0.001 && <div><span>Round off</span><b>{calc.roundOff > 0 ? '+' : '−'}{inr2(Math.abs(calc.roundOff))}</b></div>}
                                    <div className={styles.grand}><span>Bill total</span><b>{inr2(calc.total)}</b></div>
                                    {advance > 0.009 && <div className={styles.green}><span>Advance used</span><b>−{inr2(Math.min(advance, calc.total))}</b></div>}
                                    {payNow > 0 && <div className={styles.green}><span>Paid now</span><b>−{inr2(payNow)}</b></div>}
                                    <div style={{ fontWeight: 900 }}><span>Left to pay</span><b className={calc.total - Math.min(advance, calc.total) - payNow > 0.01 ? styles.red : styles.green}>{inr2(Math.max(0, calc.total - Math.min(advance, calc.total) - payNow))}</b></div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.modalFoot}>
                    <span className={styles.muted}>{calc.rows.filter((l) => l.productId).length} products · {calc.rows.reduce((s, l) => s + (l.productId ? l.qty : 0), 0)} units</span>
                    <div className={styles.footRight}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving || loading}>{saving && <Loader2 size={14} className={styles.spin} />} Save bill and add stock</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
